import type { ParsedBrickCell } from "./AsciiBrickMapParser.js";
import { ALL_MELODIES, NOTE_HZ_BY_ID, type MelodyDef } from "../data/noteCatalog.js";

/**
 * 멜로디 블록 챕터 레이아웃 — 순수 함수 (Phaser 비의존).
 *
 * 절차 생성(ChapterBrickLayout)을 대체하는 분기. 곡 음 시퀀스를 격자 셀로 펼치되 **발사구에 가까운(아래)
 * 행부터 곡 첫 음**을 배치한다(사용자 결정 "배치+커서 혼합"의 배치 측면). 모든 셀은 1×1.
 *
 * ⚠ HP 곡선 보존: 셀 수가 곡마다 달라도 `hpMultiplier = melodyHpBudget / 음수`로 균등 분배해
 * Σ hpMultiplier = melodyHpBudget(절차 seedHpBudget과 동일값)을 유지한다. 따라서 총 HP =
 * computeBrickHp(chapter) × melodyHpBudget으로 절차 챕터와 같은 증가 곡선을 그린다.
 */

/**
 * 곡 음 id 배열 → 셀 배열. 가까운(맨 아래) 행 왼→오부터 곡 앞 음, 위로 갈수록 곡 뒷 음.
 * 셀 수 = 음 수. 빈 곡이면 []. layoutBrickCells로 픽셀 좌표 변환 시 noteId가 보존된다.
 */
export function buildMelodyCells(
  noteIds: readonly string[],
  cols: number,
  melodyHpBudget: number,
): ParsedBrickCell[] {
  const n = noteIds.length;
  if (n === 0) return [];
  const rows = Math.ceil(n / cols);
  const hpMultiplier = melodyHpBudget / n; // 균등 — Σ = melodyHpBudget (곡선 보존).
  // 곡 내 음역 정규화 → 색 강도(낮은음=빨강 0 ~ 높은음=보라 1). 색·음 모두 noteId에서 일관 파생.
  const songHz = noteIds.map((id) => NOTE_HZ_BY_ID[id] ?? 440);
  const minHz = Math.min(...songHz);
  const range = Math.max(...songHz) - minHz || 1;
  const cells: ParsedBrickCell[] = [];
  for (let i = 0; i < n; i++) {
    const rowFromBottom = Math.floor(i / cols); // 0 = 맨 아래(발사구 가까움)
    const col = i % cols;
    const row = rows - 1 - rowFromBottom; // grid row(0=위)로 변환 → 곡 앞 음이 아래
    cells.push({
      col,
      row,
      kind: "normal",
      widthCells: 1,
      heightCells: 1,
      hpMultiplier,
      noteId: noteIds[i]!,
      strength: (songHz[i]! - minHz) / range,
    });
  }
  return cells;
}

/** 챕터 레이아웃 분기 결과 — 멜로디(곡 음 + id) 또는 절차 생성. */
export type ChapterLayout =
  | { kind: "melody"; melodyId: string; noteIds: readonly string[] }
  | { kind: "procedural" };

/**
 * 챕터 번호 → 레이아웃 분기. `chapter % melodyEvery === 0`이면 멜로디 챕터(곡은 enable 목록 순환),
 * 아니면 절차 생성. melodyEvery≤0 또는 곡 없음이면 항상 절차(기능 off 안전).
 * 곡 순환: chapter/melodyEvery(1-based) → (index-1) % 곡수. 예 every=5: 5→곡0, 10→곡1, 15→곡2, 20→곡0.
 */
export function resolveChapterLayout(
  chapter: number,
  melodyEvery: number,
  melodies: readonly MelodyDef[] = ALL_MELODIES,
): ChapterLayout {
  if (melodyEvery > 0 && melodies.length > 0 && chapter > 0 && chapter % melodyEvery === 0) {
    const index = (chapter / melodyEvery - 1) % melodies.length;
    const m = melodies[index]!;
    return { kind: "melody", melodyId: m.melodyId, noteIds: m.noteIds };
  }
  return { kind: "procedural" };
}
